// src/pages/Gallery.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaImages, FaCalendar, FaTimes, FaChevronLeft, FaChevronRight } from "react-icons/fa";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

function Gallery() {
  const [dateOptions, setDateOptions] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [images, setImages] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [perPage] = useState(12);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalIndex, setModalIndex] = useState(0);

  useEffect(() => {
    async function fetchDates() {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/gallery_dates`);
        setDateOptions(res.data.dates);
      } catch {
        setDateOptions([]);
      }
    }
    fetchDates();
  }, []);

  useEffect(() => {
    setLoading(true);
    let params = `?page=${page}&per_page=${perPage}`;
    if (selectedDate) params += `&date=${selectedDate}`;
    axios
      .get(`${API_BASE_URL}/api/gallery${params}`)
      .then((res) => { setImages(res.data.images); setTotal(res.data.total); })
      .catch(() => { setImages([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [selectedDate, page, perPage]);

  useEffect(() => {
    if (!modalOpen) return;
    const handleKey = (e) => {
      if (e.key === "ArrowRight") setModalIndex((i) => (i + 1) % images.length);
      else if (e.key === "ArrowLeft") setModalIndex((i) => (i - 1 + images.length) % images.length);
      else if (e.key === "Escape") setModalOpen(false);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [modalOpen, images.length]);

  const openModal = (i) => { setModalIndex(i); setModalOpen(true); };
  const closeModal = () => setModalOpen(false);
  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="p-6 max-w-screen-xl space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Gallery</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {total > 0 ? `${total} image${total !== 1 ? "s" : ""}` : "Browse captured images"}
          </p>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-2">
          <FaCalendar className="text-slate-400 text-sm" />
          <select
            value={selectedDate}
            onChange={(e) => { setSelectedDate(e.target.value); setPage(1); }}
            className="text-sm border border-slate-200 rounded-xl px-3 py-2 bg-white
              focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition"
          >
            <option value="">All Dates</option>
            {dateOptions.map((d, i) => <option key={i} value={d}>{d}</option>)}
          </select>
        </div>
      </div>

      {/* Grid */}
      <div className="card">
        {loading ? (
          <div className="flex items-center justify-center py-24 gap-3 text-slate-400">
            <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm">Loading images…</span>
          </div>
        ) : images.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3">
            <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center">
              <FaImages className="text-2xl text-slate-300" />
            </div>
            <div className="text-sm font-medium text-slate-500">No images found</div>
            {selectedDate && (
              <button onClick={() => setSelectedDate("")}
                className="text-xs text-blue-600 hover:underline">Clear filter</button>
            )}
          </div>
        ) : (
          <div className="p-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {images.map((img, i) => (
              <div
                key={i}
                className="group rounded-2xl overflow-hidden border border-slate-100 bg-slate-50
                  cursor-pointer transition-all duration-200 hover:shadow-card-md hover:-translate-y-0.5"
                onClick={() => openModal(i)}
              >
                <div className="relative overflow-hidden">
                  <img
                    src={API_BASE_URL + img.url}
                    alt={img.name}
                    className="w-full h-40 object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-200" />
                </div>
                <div className="px-3 py-2.5">
                  <div className="text-xs font-semibold text-slate-700 truncate">{img.name}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 px-5 pb-5">
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              className="btn-secondary disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <FaChevronLeft className="text-xs" /> Prev
            </button>
            <span className="text-sm text-slate-500">
              Page <span className="font-semibold text-slate-800">{page}</span> of {totalPages}
            </span>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="btn-secondary disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next <FaChevronRight className="text-xs" />
            </button>
          </div>
        )}
      </div>

      {/* Lightbox modal */}
      {modalOpen && images.length > 0 && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center animate-fadeIn"
          onClick={closeModal}
        >
          <button
            className="absolute top-4 right-4 w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20
              text-white flex items-center justify-center transition"
            onClick={closeModal}
          >
            <FaTimes size={14} />
          </button>
          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-xl
              bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
            onClick={(e) => { e.stopPropagation(); setModalIndex((modalIndex - 1 + images.length) % images.length); }}
          >
            <FaChevronLeft />
          </button>
          <img
            src={API_BASE_URL + images[modalIndex].url}
            alt=""
            className="max-w-[85vw] max-h-[85vh] rounded-2xl shadow-2xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-xl
              bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
            onClick={(e) => { e.stopPropagation(); setModalIndex((modalIndex + 1) % images.length); }}
          >
            <FaChevronRight />
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-xs text-white/60">
            {modalIndex + 1} / {images.length}
          </div>
        </div>
      )}
    </div>
  );
}

export default Gallery;
